/**
 * One campaign Morcast runs, as creators see it: its key figures, taking part beside the rest on
 * large screens and first on small ones, its result and review once published (specification
 * §10), its brief, how creators are paid (§9), and its record on-chain. Anyone can open it.
 */

import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router";

import { BriefCard } from "../../campaigns/BriefCard.js";
import { type PublicCampaign, useCampaign } from "../../campaigns/campaigns.js";
import { PhaseBadge } from "../../campaigns/PhaseBadge.js";
import { ResultCard } from "../../campaigns/ResultCard.js";
import { ReviewCard } from "../../campaigns/ReviewCard.js";
import { creatorsPool, timingLine } from "../../campaigns/timing.js";
import { AddressLink, TransactionLink } from "../../components/ChainLinks.js";
import { BrandMark } from "../../components/marks.js";
import {
  ErrorMessage,
  Fact,
  Loading,
  PageHeader,
  Section,
  Stat,
  styles,
} from "../../components/ui.js";
import { formatAmount, formatCount, formatWindow } from "../../format.js";
import { platformLabel } from "./RequestList.js";
import { TakePart } from "./TakePart.js";

export function CampaignPage() {
  const { escrow = "", id = "" } = useParams();
  const campaign = useCampaign(escrow, id);

  return (
    <div className={styles.page}>
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
      >
        <ArrowLeft aria-hidden className="size-4" />
        All campaigns
      </Link>
      <div className="mt-4">
        {campaign.isPending && <Loading />}
        {campaign.isError && <ErrorMessage error={campaign.error} />}
        {campaign.data && <CampaignView campaign={campaign.data} />}
      </div>
    </div>
  );
}

function CampaignView({ campaign }: { campaign: PublicCampaign }) {
  const { metric, token } = campaign;

  return (
    <>
      <PageHeader
        eyebrow={`${campaign.brand.name} · ${platformLabel(campaign)}`}
        title={campaign.brief.title}
        leading={<BrandMark name={campaign.brand.name} size="lg" />}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <PhaseBadge phase={campaign.phase} />
          <span className="text-sm">
            {formatWindow(campaign.platform, campaign.startAt, campaign.endAt)}
          </span>
        </div>
      </PageHeader>

      <dl className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat
          label="Budget"
          value={formatAmount(campaign.budget, token)}
          hint="Locked in the escrow"
        />
        <Stat
          label="Creators share up to"
          value={formatAmount(creatorsPool(campaign), token)}
          hint="80% of the budget, at the target"
        />
        <Stat label="Target" value={formatCount(campaign.target)} hint={metric.unit} />
        <Stat
          label="Creator threshold"
          value={formatCount(campaign.threshold)}
          hint="A creator's total counts from here"
        />
        <Stat
          label="Timing"
          value={timingLine(campaign)}
          hint={`${campaign.submissionLimit} submission${campaign.submissionLimit === 1 ? "" : "s"} per account, up to ${campaign.accountLimit} account${campaign.accountLimit === 1 ? "" : "s"}`}
        />
      </dl>

      <div className="grid gap-x-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <aside aria-label="Taking part" className="lg:order-2">
          <div className="lg:sticky lg:top-20">
            <TakePart campaign={campaign} />
          </div>
        </aside>
        <div className="min-w-0 lg:order-1">
          <ResultCard campaign={campaign} />
          <ReviewCard campaign={campaign} />
          <BriefCard brief={campaign.brief} />
          <Section title="How creators are paid" className="mt-6 text-sm">
            <ul className="list-disc space-y-1.5 pl-5 text-zinc-700 dark:text-zinc-300">
              <li>
                Your result is the {metric.unit} of your accepted content. {metric.description}
              </li>
              <li>
                A total below the threshold of {formatCount(campaign.threshold)} scores 0. Above it,
                your score is your total.
              </li>
              <li>
                The brand pays for all scores together, up to the target. That is the whole budget
                once {formatCount(campaign.target)} is reached, and less in proportion below it.
              </li>
              <li>
                80% of that goes to creators in proportion to their scores and 20% to Morcast. The
                rest of the budget returns to the brand. You claim your payout from the escrow
                whenever you like.
              </li>
            </ul>
          </Section>
          <Section
            title="On-chain record"
            description="Anyone can check the campaign's terms and deposit on Base."
            className="mt-6"
          >
            <dl className="grid gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
              <Fact label="Brand">
                {campaign.brand.name}
                <span className="block">
                  <AddressLink chainId={campaign.chainId} address={campaign.brand.wallet} />
                </span>
              </Fact>
              <Fact label="Escrow">
                <AddressLink chainId={campaign.chainId} address={campaign.escrow} />
              </Fact>
              <Fact label="Campaign ID">{campaign.id}</Fact>
              <Fact label="Deposit">
                <TransactionLink chainId={campaign.chainId} hash={campaign.createdTx} />
              </Fact>
              <Fact label="Manifest">
                <a
                  href={`/api/manifests/${campaign.manifestHash}`}
                  className="font-mono text-xs break-all hover:underline"
                >
                  {campaign.manifestHash}
                </a>
                {campaign.manifestCopyUrl && (
                  <a
                    href={campaign.manifestCopyUrl}
                    className="mt-1 block text-xs font-normal underline"
                  >
                    Permanent copy
                  </a>
                )}
                {campaign.manifestArchiveUrl && (
                  <a
                    href={campaign.manifestArchiveUrl}
                    className="mt-1 block text-xs font-normal underline"
                  >
                    Copy on Arweave
                  </a>
                )}
              </Fact>
            </dl>
          </Section>
        </div>
      </div>
    </>
  );
}
